import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ResourceError } from "../../../utils/ResourceError";
import { DeviationDeliveryHealth } from "./DeviationDeliveryHealth";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { SaveBar } from "../../../utils/SaveBar";
import {
  fetchDeviationNotificationSettings,
  updateDeviationNotificationSettings,
} from "../../../../api/quality/deviationNotificationSettings";

const emptySettings = {
  send_mode: "immediate",
  copy_sender: true,
  digest_hour: 17,
};

const normalize = (data = {}) => ({
  send_mode: data.send_mode === "digest" ? "digest" : "immediate",
  copy_sender: data.copy_sender !== false,
  digest_hour: Number.isInteger(data.digest_hour) ? data.digest_hour : 17,
});

const snapshotOf = (data) =>
  JSON.stringify({
    send_mode: data.send_mode === "digest" ? "digest" : "immediate",
    copy_sender: data.copy_sender !== false,
    digest_hour: Number(data.digest_hour),
  });

export function DeviationNotificationSettingsForm() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [health, setHealth] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [settings, setSettings] = useState(emptySettings);
  const [savedSnapshot, setSavedSnapshot] = useState(snapshotOf(emptySettings));

  const applyLoaded = useCallback((data) => {
    setHealth(data.delivery_health || null);
    const next = normalize(data);
    setSettings(next);
    setSavedSnapshot(snapshotOf(next));
  }, []);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchDeviationNotificationSettings();
      applyLoaded(data);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [applyLoaded, t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const hasChanges = useMemo(
    () => snapshotOf(settings) !== savedSnapshot,
    [settings, savedSnapshot]
  );

  const patchSettings = (patch) => {
    setSaveSuccess(false);
    setSaveError(false);
    setSettings((prev) => ({ ...prev, ...patch }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(false);
    try {
      const data = await updateDeviationNotificationSettings({
        send_mode: settings.send_mode,
        copy_sender: settings.copy_sender === true,
        digest_hour: Number(settings.digest_hour),
      });
      applyLoaded(data);
      setSaveSuccess(true);
      return true;
    } catch {
      setSaveError(true);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const digestHours = Array.from({ length: 24 }, (_, hour) => hour);

  if (loading) {
    return <ResourceLoading />;
  }

  if (loadError) return <ResourceError errors={[t("deviation.settings.load_failed")]} />;

  return (
    <>
      <fieldset disabled={saving} className="mx-auto min-w-0 max-w-3xl space-y-4 pb-28">
        <DeviationDeliveryHealth health={health} />
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-4">
            <h3 className="text-base font-semibold text-slate-900">
              {t("deviation.settings.send_mode.title")}
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              {t("deviation.settings.send_mode.description")}
            </p>
          </div>
          <div className="space-y-3">
            {[
              {
                value: "immediate",
                label: t("deviation.settings.send_mode.immediate"),
                help: t("deviation.settings.send_mode.immediate_help"),
              },
              {
                value: "digest",
                label: t("deviation.settings.send_mode.digest"),
                help: t("deviation.settings.send_mode.digest_help"),
              },
            ].map((option) => (
              <label
                key={option.value}
                className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-3 ${
                  settings.send_mode === option.value
                    ? "border-[#1463C7] bg-[#EAF3FD]"
                    : "border-slate-200 bg-slate-50/50"
                }`}
              >
                <input
                  type="radio"
                  name="deviation_send_mode"
                  checked={settings.send_mode === option.value}
                  onChange={() => patchSettings({ send_mode: option.value })}
                  className="mt-1 h-4 w-4 border-slate-300 text-[#1463C7] focus:ring-[#1463C7]"
                />
                <div>
                  <div className="text-sm font-medium text-slate-900">
                    {option.label}
                  </div>
                  <div className="text-sm text-slate-600">{option.help}</div>
                </div>
              </label>
            ))}
          </div>

          {settings.send_mode === "digest" && (
            <label className="mt-4 block text-sm font-medium text-slate-700">
              {t("deviation.settings.digest_hour")}
              <select
                value={settings.digest_hour}
                onChange={(event) =>
                  patchSettings({
                    digest_hour: parseInt(event.target.value, 10),
                  })
                }
                className="mt-2 block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
              >
                {digestHours.map((hour) => (
                  <option key={hour} value={hour}>
                    {String(hour).padStart(2, "0")}:00
                  </option>
                ))}
              </select>
            </label>
          )}
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-4">
            <h3 className="text-base font-semibold text-slate-900">
              {t("deviation.settings.copy_sender.title")}
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              {t("deviation.settings.copy_sender.description")}
            </p>
          </div>
          <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50/50 p-3">
            <input
              type="checkbox"
              checked={settings.copy_sender === true}
              onChange={(event) =>
                patchSettings({ copy_sender: event.target.checked })
              }
              className="mt-1 h-4 w-4 rounded border-slate-300 text-[#1463C7] focus:ring-[#1463C7]"
            />
            <div className="text-sm font-medium text-slate-900">
              {t("deviation.settings.copy_sender.toggle")}
            </div>
          </label>
        </section>
      </fieldset>

      <SaveBar
        hasChanges={hasChanges}
        isSaving={saving}
        isSuccess={saveSuccess}
        isError={saveError}
        successMessage={t("deviation.settings.saved")}
        errorMessage={t("deviation.settings.save_failed")}
        onSave={handleSave}
        onDismiss={() => setSaveError(false)}
        saveLabel={t("Save")}
      />
    </>
  );
}
